import React from 'react';
import { Check } from 'lucide-react';

// Card
export const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`app-card bg-white rounded-lg border border-gray-200 ${className}`}>
    {children}
  </div>
);

// Badge
export const Badge: React.FC<{ children: React.ReactNode; variant?: 'success' | 'warning' | 'danger' | 'neutral'; className?: string }> = ({ children, variant = 'neutral', className = '' }) => {
  const styles = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    danger: 'bg-red-100 text-red-800',
    neutral: 'bg-gray-100 text-gray-800'
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[variant]} ${className}`}>
      {children}
    </span>
  );
};

// Button
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  size?: 'sm' | 'md' | 'lg';
}
export const Button: React.FC<ButtonProps> = ({ variant = 'primary', size = 'md', className = '', ...props }) => {
  const base = "app-button inline-flex items-center justify-center border font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed motion-reduce:transition-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-usda-500 transition-colors";
  
  const sizes = {
    sm: 'min-h-9 px-3 py-2 text-sm',
    md: 'min-h-11 px-4 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  const variants = {
    primary: "border-transparent text-white bg-app-control hover:bg-usda-900",
    secondary: "border-transparent text-usda-700 bg-usda-100 hover:bg-usda-200",
    outline: "border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
    danger: "border-transparent text-white bg-red-600 hover:bg-red-700"
  };
  
  return <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} {...props} />;
};

interface AssociationChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  selected?: boolean;
}

export const AssociationChip: React.FC<AssociationChipProps> = ({
  icon: Icon,
  label,
  selected = false,
  className = '',
  ...props
}) => (
  <button
    type="button"
    className={`group relative inline-flex min-h-[5.5rem] min-w-[5.5rem] max-w-[8rem] flex-col items-center justify-center gap-1 rounded-2xl border px-3 py-2 text-center text-sm font-medium focus:outline-none focus:ring-2 focus:ring-usda-500 focus:ring-offset-2 ${selected ? 'border-usda-700 bg-usda-100 text-usda-900' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'} ${className}`}
    {...props}
  >
    <Icon className={`h-6 w-6 shrink-0 ${selected ? 'text-usda-800' : 'text-gray-500 group-hover:text-gray-700'}`} aria-hidden="true" />
    <span className="max-w-full break-words text-xs leading-tight sm:text-sm">{label}</span>
    <span className={`absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border shadow-sm ${selected ? 'border-usda-700 bg-usda-700 text-white' : 'border-gray-300 bg-white text-transparent'}`}>
      <Check className="h-3 w-3" aria-hidden="true" />
    </span>
  </button>
);
